import React, { useMemo, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Slider } from "./ui/slider";
import { Label } from "./ui/label";
import { Layers } from "lucide-react";

const ROOF = 5, FOOT = 3, SCALE = 6;
const FLOOR_COLORS = ["#38BDF8", "#A855F7", "#34D399", "#F59E0B"];

export default function CrossSection({ open, onOpenChange, blueprint, floors, site, wallH = 9 }) {
  const list = (floors && floors.length) ? floors : [{ name: "Ground Floor", rooms: blueprint?.rooms || [] }];
  const rooms0 = list[0]?.rooms || [];
  const slopeType = site?.slope_type || "Flat";
  const grade = Math.max(0, Math.min(100, site?.slope_pct || 0));
  const sloped = slopeType !== "Flat" && grade > 0;
  const axis = slopeType === "Cross-slope" ? "y" : "x";
  const perp = axis === "x" ? "y" : "x";
  const aLenKey = axis === "x" ? "w" : "h";
  const pLenKey = perp === "x" ? "w" : "h";

  const bounds = useMemo(() => {
    if (!rooms0.length) return null;
    const aMin = Math.min(...rooms0.map((r) => r[axis]));
    const aMax = Math.max(...rooms0.map((r) => r[axis] + r[aLenKey]));
    const pMin = Math.min(...rooms0.map((r) => r[perp]));
    const pMax = Math.max(...rooms0.map((r) => r[perp] + r[pLenKey]));
    return { aMin, aMax, pMin, pMax };
  }, [rooms0, axis, perp]);

  const [cutPos, setCutPos] = useState(0);
  useEffect(() => { if (bounds) setCutPos(Math.round((bounds.pMin + bounds.pMax) / 2)); }, [bounds?.pMin, bounds?.pMax]);

  if (!bounds) return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
        <DialogHeader><DialogTitle className="font-heading">Cross-Section</DialogTitle>
          <DialogDescription className="text-[#8B949E]">Add rooms to see a section.</DialogDescription></DialogHeader>
      </DialogContent>
    </Dialog>
  );

  const { aMin, aMax, pMin, pMax } = bounds;
  const n = list.length;
  const runFull = aMax - aMin;
  const drop = sloped ? (grade / 100) * runFull : 0;
  const vdrop = Math.min(drop, 34);
  const eLeft = sloped ? (slopeType === "Uphill" ? -vdrop / 2 : vdrop / 2) : 0;
  const eRight = sloped ? (slopeType === "Uphill" ? vdrop / 2 : -vdrop / 2) : 0;
  const padY = 40 + (ROOF + n * wallH) * SCALE;
  const bottomY = padY + (FOOT + vdrop / 2 + 6) * SCALE;
  const X = (a) => 60 + (a - aMin) * SCALE;
  const EY = (e) => padY - e * SCALE;
  const svgW = runFull * SCALE + 120;

  const sliced = (rs) => (rs || []).filter((r) => r[perp] <= cutPos && cutPos <= r[perp] + r[pLenKey]);
  const soil = `${X(aMin)},${EY(eLeft)} ${X(aMax)},${EY(eRight)} ${X(aMax)},${bottomY} ${X(aMin)},${bottomY}`;

  // mini top-view plan
  const MS = 2.6, mpad = 8;
  const mW = (aMax - aMin) * MS + mpad * 2, mH = (pMax - pMin) * MS + mpad * 2;
  const MX = (a) => mpad + (a - aMin) * MS;
  const MP = (p) => mpad + (p - pMin) * MS;
  const cutScreen = MP(cutPos);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#161B22] border-[#30363D] text-[#F0F6FC] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-xl">
            <Layers className="w-5 h-5 text-[#F59E0B]" /> Cross-Section (Side Elevation)
          </DialogTitle>
          <DialogDescription className="text-[#8B949E]">Drag the cut line across the plan to slice the house — the elevation updates live.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad mb-1">Cut line (top view)</div>
            <svg viewBox={`0 0 ${mW} ${mH}`} width="150" height={150 * (mH / mW)} data-testid="section-mini-plan" style={{ background: "#0B1017", borderRadius: 8, border: "1px solid #30363D" }}>
              {rooms0.map((r) => (
                <rect key={r.id} x={MX(r.x)} y={MP(r.y)} width={r.w * MS} height={r.h * MS} fill="rgba(240,246,252,0.05)" stroke="#8B949E" strokeWidth="0.6" />
              ))}
              {axis === "x"
                ? <line x1={mpad} y1={cutScreen} x2={mW - mpad} y2={cutScreen} stroke="#F43F5E" strokeWidth="2" strokeDasharray="3 2" />
                : <line x1={cutScreen} y1={mpad} x2={cutScreen} y2={mH - mpad} stroke="#F43F5E" strokeWidth="2" strokeDasharray="3 2" />}
            </svg>
          </div>
          <div className="flex-1">
            <Label className="text-xs text-[#8B949E]">Cut position ({cutPos} ft on {perp.toUpperCase()}-axis)</Label>
            <Slider value={[cutPos]} min={Math.floor(pMin)} max={Math.ceil(pMax)} step={1} onValueChange={(v) => setCutPos(v[0])} className="mt-3" data-testid="section-cut-slider" />
            <div className="text-xs text-[#8B949E] mt-2 font-mono-cad">{sliced(rooms0).length} room(s) intersected on ground floor</div>
          </div>
        </div>

        <div className="rounded-xl border border-[#30363D] bg-[#090D12] overflow-hidden mt-2">
          <svg viewBox={`0 0 ${svgW} ${bottomY + 40}`} width="100%" height="340" data-testid="cross-section-svg" style={{ display: "block" }}>
            <polygon points={soil} fill="#2a2016" stroke="#3a2f22" strokeWidth="1" />
            <line x1={X(aMin)} y1={EY(0)} x2={X(aMax)} y2={EY(0)} stroke="#F59E0B" strokeDasharray="4 3" strokeWidth="1.2" />
            <line x1={X(aMin)} y1={EY(eLeft)} x2={X(aMax)} y2={EY(eRight)} stroke="#6E7681" strokeWidth="2" />
            {sloped && <text x={X(aMin + runFull / 2)} y={EY((eLeft + eRight) / 2) - 8} fill="#6E7681" fontSize="11" fontFamily="JetBrains Mono, monospace" textAnchor="middle">natural grade · {grade}%</text>}

            {list.map((fl, fi) => {
              const yTop = EY((fi + 1) * wallH), yBot = EY(fi * wallH);
              const color = FLOOR_COLORS[fi % 4];
              return sliced(fl.rooms).map((r) => (
                <g key={`${fi}-${r.id}`}>
                  <rect x={X(r[axis])} y={yTop} width={r[aLenKey] * SCALE} height={yBot - yTop} fill={`${color}22`} stroke={color} strokeWidth="1.5" />
                  <text x={X(r[axis]) + r[aLenKey] * SCALE / 2} y={(yTop + yBot) / 2 + 4} fill={color} fontSize="9" fontFamily="Outfit, sans-serif" fontWeight="600" textAnchor="middle">{r.name}</text>
                </g>
              ));
            })}

            {(() => {
              const s0 = sliced(rooms0);
              if (!s0.length) return null;
              const bx0 = X(Math.min(...s0.map((r) => r[axis])));
              const bx1 = X(Math.max(...s0.map((r) => r[axis] + r[aLenKey])));
              return (
                <g>
                  <rect x={bx0} y={EY(0)} width={bx1 - bx0} height={FOOT * SCALE} fill="#21262D" stroke="#484F58" strokeWidth="1.5" />
                  <text x={(bx0 + bx1) / 2} y={EY(0) + FOOT * SCALE + 12} fill="#8B949E" fontSize="9" fontFamily="JetBrains Mono, monospace" textAnchor="middle">footing</text>
                  {sloped && (() => {
                    const highLeft = slopeType !== "Uphill";
                    const wx = highLeft ? bx0 - 10 : bx1 + 10;
                    const topE = highLeft ? eLeft : eRight;
                    return (
                      <g>
                        <rect x={wx - 5} y={EY(topE)} width={10} height={EY(-FOOT) - EY(topE)} fill="#F43F5E" fillOpacity="0.4" stroke="#F43F5E" strokeWidth="1.5" />
                        <text x={wx} y={EY(topE) - 6} fill="#F43F5E" fontSize="9" fontFamily="JetBrains Mono, monospace" textAnchor="middle">retaining {Math.round(Math.abs(topE) + FOOT)}′</text>
                      </g>
                    );
                  })()}
                </g>
              );
            })()}
          </svg>
        </div>
      </DialogContent>
    </Dialog>
  );
}
